import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';
import { ApiService } from '../services/api.service';
import { ContextService } from '../services/context.service';
import { PaymentService } from '../services/payment.service';

type TeamsContextState = 'loading' | 'noStorage' | 'noLicense' | 'ready';

@Component({
  selector: 'app-teams-overview',
  templateUrl: './teams-overview.component.html',
  styleUrls: ['./teams-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TeamsOverviewComponent implements OnInit, OnDestroy {
  private readonly subscription = new Subscription();

  public readonly contextState = signal<TeamsContextState>('loading');
  public readonly flows = signal<any[]>([]);
  public readonly runs = signal<any[]>([]);
  public readonly accounts = signal<any[]>([]);
  public readonly scans = signal<any[]>([]);
  public readonly sourceWarning = signal('');
  public readonly workspaceId = signal<string | null>(null);

  public readonly monitoredFlows = computed(() => this.flows().filter((flow) => !!flow?.monitor));
  public readonly failedRuns = computed(() => this.runs().filter((run) => `${run?.status || ''}`.toLowerCase() === 'failed').length);
  public readonly successfulRuns = computed(() => this.runs().filter((run) => `${run?.status || ''}`.toLowerCase() === 'succeeded').length);
  public readonly successRate = computed(() => {
    const decided = this.successfulRuns() + this.failedRuns();
    return decided ? this.successfulRuns() / decided : null;
  });
  public readonly members = computed(() => this.paymentService.subscription$.value?.users || []);
  public readonly teamsLicensedMembers = computed(() => this.members().filter((member) => !!member.license_teams).length);
  public readonly workspaceSeatLimit = computed(() => {
    const [workspace] = this.paymentService.workspace() || [];
    return workspace?.seats_teams ?? null;
  });
  public readonly latestScan = computed(() => {
    return this.scans().slice().sort((left, right) => this.scanTime(right) - this.scanTime(left))[0] || null;
  });

  constructor(
    public paymentService: PaymentService,
    private contextService: ContextService,
    private apiService: ApiService
  ) {}

  public async ngOnInit(): Promise<void> {
    try {
      const context = await this.contextService.load();
      if (!context?.workspace || !context.hasTeamsEntitlement) {
        this.contextState.set('noLicense');
        return;
      }
      if (!context.hasWorkspaceStorage) {
        this.contextState.set('noStorage');
        return;
      }

      this.workspaceId.set(context.workspace || null);
      this.accounts.set(context.accounts || []);
      this.contextState.set('ready');

      this.subscription.add(combineLatest([this.apiService.flow$, this.apiService.run$]).subscribe(([flows, runs]) => {
        if (flows !== null) this.flows.set(flows || []);
        if (runs !== null) this.runs.set(runs || []);
      }));

      this.subscription.add(this.apiService.run$.subscribe((runs) => {
        if (runs === null) this.apiService.watchRuns(-7).catch(() => this.sourceWarning.set('Run history is temporarily unavailable.'));
      }));

      this.apiService.queryScansHttp()
        .then((scans) => this.scans.set(scans || []))
        .catch(() => this.sourceWarning.set('Scan status is temporarily unavailable.'));
    } catch {
      this.contextState.set('noLicense');
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public latestScanLabel(): string {
    const scan = this.latestScan();
    const value = scan?.end || scan?.start;
    if (!value) return 'No scans yet';
    const date = new Date(value);
    if (Number.isNaN(date.valueOf())) return 'Unavailable';
    return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
  }

  public latestScanCoverage(): string {
    const scan = this.latestScan();
    if (!scan) return 'Connect Power Automate and Power Apps to begin inventory scanning.';
    return `${scan.envs || 0} environments · ${scan.flows || 0} flows · ${scan.apps || 0} apps`;
  }

  private scanTime(scan: any): number {
    const value = new Date(scan?.end || scan?.start || 0).valueOf();
    return Number.isNaN(value) ? 0 : value;
  }
}
